import { TLogLevel } from '@streaming/mouflon-utils';

import { availableAbrControllerConfigValues, defaultAbrControllerConfig, IAbrControllerConfig } from '@abrController/AbrController.config';
import {
  availableSwitchHistoryConfigValues,
  defaultSwitchHistoryConfig,
  ISwitchHistoryConfig,
} from '@abrController/DataCollectors/SwitchHistory/SwitchHistory.config';
import {
  availableThroughputHistoryConfigValues,
  defaultThroughputHistoryConfig,
  IThroughputHistoryConfig,
} from '@abrController/DataCollectors/ThroughputHistory/ThroughputHistory.config';
import {
  availableDeltaBufferEstimatorConfigValues,
  defaultDeltaBufferEstimatorConfig,
  IDeltaBufferEstimatorConfig,
} from '@abrController/Estimators/DeltaBufferEstimator/DeltaBufferEstimaotr.config';
import {
  availableDroppedFramesEstimatorConfigValues,
  defaultDroppedFramesEstimatorConfig,
  IDroppedFramesEstimatorConfig,
} from '@abrController/Estimators/DroppedFramesEstimator/DroppedFramesEstimator.config';
import {
  availableInsufficientBufferEstimatorConfigValues,
  defaultInsufficientBufferEstimatorConfig,
  IInsufficientBufferEstimatorConfig,
} from '@abrController/Estimators/InsufficientBufferEstimator/InsufficientBufferEstimator.config';
import {
  availableMinimalBufferToIncreaseEstimatorConfigValues,
  defaultMinimalBufferToIncreaseEstimatorConfig,
  IMinimalBufferToIncreaseEstimatorConfig,
} from '@abrController/Estimators/MinimalBufferToIncreaseEstimator/MinimalBufferToIncreaseEstimator.config';
import {
  availableThroughputEstimatorValues,
  defaultThroughputEstimatorConfig,
  IThroughputEstimatorConfig,
} from '@abrController/Estimators/ThroughputEstimator/ThroughputEstimator.config';
import {
  availableBufferControllerConfigValues,
  defaultBufferControllerConfig,
  IBufferControllerConfig,
} from '@bufferController/BufferController.config';
import {
  availableBufferStallsDetectorConfigValues,
  defaultBufferStallsDetectorConfig,
  IBufferStallsDetectorConfig,
} from '@bufferController/BufferStallsDetector/BufferStallsDetector.config';
import {
  availableOriginalTargetBufferDecreaserConfigValues,
  defaultOriginalTargetBufferDecreaserConfig,
  IOriginalTargetBufferDecreaserConfig,
} from '@bufferController/PlaybackSpeedController/OriginalTargetBufferDecreaser/OriginalTargetBufferDecreaser.config';
import {
  availablePlaybackSpeedActuatorConfigValues,
  defaultPlaybackSpeedActuatorConfig,
  IPlaybackSpeedActuatorConfig,
} from '@bufferController/PlaybackSpeedController/PlaybackSpeedActuator/PlaybackSpeedActuator.config';
import {
  availablePlaybackSpeedWatcherConfigValues,
  defaultPlaybackSpeedWatcherConfig,
  IPlaybackSpeedWatcherConfig,
} from '@bufferController/PlaybackSpeedController/PlaybackSpeedWatcher/PlaybackSpeedWatcher.config';
import {
  availableTargetBufferDecreaserConfigValues,
  defaultTargetBufferDecreaserConfig,
  ITargetBufferDecreaserConfig,
} from '@bufferController/PlaybackSpeedController/TargetBufferDecreaser/TargetBufferDecreaser.config';
import {
  availableTargetBufferIncreaserConfigValues,
  defaultTargetBufferIncreaserConfig,
  ITargetBufferIncreaserConfig,
} from '@bufferController/PlaybackSpeedController/TargetBufferIncreaser/TargetBufferIncreaser.config';
import {
  availablePlaybackStateControllerConfig,
  defaultPlaybackStateControllerConfig,
  IPlaybackStateControllerConfig,
} from '@bufferController/PlaybackStateController/PlaybackStateController.config';
import { TAvailableConfigValues } from '@customTypes/common';
import {
  availableFragmentParserConfigValues,
  defaultFragmentParserConfig,
  IFragmentParserConfig,
} from '@fragmentParser/FragmentParser.config';
import {
  availableFragmentsFilterConfigValues,
  defaultFragmentsFilterConfig,
  IFragmentsFilterConfig,
} from '@fragmentsFilter/FragmentsFilter.config';
import {
  availableResolutionManagerConfigValues,
  defaultResolutionManagerConfig,
  IResolutionManagerConfig,
} from '@resolutionsManager/ResolutionManager.config';
import {
  IBasePlaylistRotationControllerConfig,
  availableBasePlaylistRotationControllerConfigValues,
  defaultBasePlaylistRotationControllerConfig,
} from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.config';
import {
  IFragmentLoaderConfig,
  availableFragmentLoaderConfigValues,
  defaultFragmentLoaderConfig,
} from '@streamLoader/FragmentLoader/FragmentLoader.config';

import { availableDebuggerConfigValues, defaultDebuggerConfig, IDebuggerConfig } from './Debugger/Debugger.config';
import {
  availableFragmentsBufferConfigValues,
  defaultFragmentsBufferConfig,
  IFragmentsBufferConfig,
} from './FragmentsBuffer/FragmentsBuffer.config';

export type TDoppioPlayerConfigLike = {
  [P in keyof IDoppioPlayerConfig]: TAvailableConfigValues<IDoppioPlayerConfig[P]>;
};

export interface IDoppioPlayerConfig {
  playlistLoader: IBasePlaylistRotationControllerConfig;
  fragmentLoader: IFragmentLoaderConfig;
  resolutionsManager: IResolutionManagerConfig;
  bufferController: IBufferControllerConfig;
  abr: IAbrControllerConfig;
  switchHistory: ISwitchHistoryConfig;
  throughputHistory: IThroughputHistoryConfig;
  throughputEstimator: IThroughputEstimatorConfig;
  droppedFramesEstimator: IDroppedFramesEstimatorConfig;
  insufficientBufferEstimator: IInsufficientBufferEstimatorConfig;
  fragmentParser: IFragmentParserConfig;
  fragmentBuffer: IFragmentsBufferConfig;
  debugger: IDebuggerConfig;
  minimalBufferToIncreaseEstimator: IMinimalBufferToIncreaseEstimatorConfig;
  deltaBufferEstimator: IDeltaBufferEstimatorConfig;
  targetBufferIncreaser: ITargetBufferIncreaserConfig;
  targetBufferDecreaser: ITargetBufferDecreaserConfig;
  originalTargetBufferDecreaser: IOriginalTargetBufferDecreaserConfig;
  playbackStateController: IPlaybackStateControllerConfig;
  playbackSpeedActuator: IPlaybackSpeedActuatorConfig;
  playbackSpeedWatcher: IPlaybackSpeedWatcherConfig;
  bufferStallsDetector: IBufferStallsDetectorConfig;
  fragmentsFilter: IFragmentsFilterConfig;
}

export const defaultConfig: IDoppioPlayerConfig = {
  playlistLoader: defaultBasePlaylistRotationControllerConfig,
  fragmentLoader: defaultFragmentLoaderConfig,
  resolutionsManager: defaultResolutionManagerConfig,
  bufferController: defaultBufferControllerConfig,
  throughputHistory: defaultThroughputHistoryConfig,
  abr: defaultAbrControllerConfig,
  switchHistory: defaultSwitchHistoryConfig,
  throughputEstimator: defaultThroughputEstimatorConfig,
  droppedFramesEstimator: defaultDroppedFramesEstimatorConfig,
  fragmentParser: defaultFragmentParserConfig,
  debugger: defaultDebuggerConfig,
  insufficientBufferEstimator: defaultInsufficientBufferEstimatorConfig,
  minimalBufferToIncreaseEstimator: defaultMinimalBufferToIncreaseEstimatorConfig,
  deltaBufferEstimator: defaultDeltaBufferEstimatorConfig,
  playbackStateController: defaultPlaybackStateControllerConfig,
  targetBufferIncreaser: defaultTargetBufferIncreaserConfig,
  targetBufferDecreaser: defaultTargetBufferDecreaserConfig,
  originalTargetBufferDecreaser: defaultOriginalTargetBufferDecreaserConfig,
  playbackSpeedActuator: defaultPlaybackSpeedActuatorConfig,
  playbackSpeedWatcher: defaultPlaybackSpeedWatcherConfig,
  bufferStallsDetector: defaultBufferStallsDetectorConfig,
  fragmentsFilter: defaultFragmentsFilterConfig,
  fragmentBuffer: defaultFragmentsBufferConfig,
};

export const availableConfigValues: TDoppioPlayerConfigLike = {
  playlistLoader: availableBasePlaylistRotationControllerConfigValues,
  fragmentLoader: availableFragmentLoaderConfigValues,
  resolutionsManager: availableResolutionManagerConfigValues,
  bufferController: availableBufferControllerConfigValues,
  throughputHistory: availableThroughputHistoryConfigValues,
  abr: availableAbrControllerConfigValues,
  switchHistory: availableSwitchHistoryConfigValues,
  throughputEstimator: availableThroughputEstimatorValues,
  droppedFramesEstimator: availableDroppedFramesEstimatorConfigValues,
  fragmentParser: availableFragmentParserConfigValues,
  debugger: availableDebuggerConfigValues,
  insufficientBufferEstimator: availableInsufficientBufferEstimatorConfigValues,
  minimalBufferToIncreaseEstimator: availableMinimalBufferToIncreaseEstimatorConfigValues,
  deltaBufferEstimator: availableDeltaBufferEstimatorConfigValues,
  playbackStateController: availablePlaybackStateControllerConfig,
  targetBufferIncreaser: availableTargetBufferIncreaserConfigValues,
  targetBufferDecreaser: availableTargetBufferDecreaserConfigValues,
  originalTargetBufferDecreaser: availableOriginalTargetBufferDecreaserConfigValues,
  playbackSpeedActuator: availablePlaybackSpeedActuatorConfigValues,
  playbackSpeedWatcher: availablePlaybackSpeedWatcherConfigValues,
  bufferStallsDetector: availableBufferStallsDetectorConfigValues,
  fragmentsFilter: availableFragmentsFilterConfigValues,
  fragmentBuffer: availableFragmentsBufferConfigValues,
};

export const DEFAULT_LOG_LEVELS: TLogLevel[] = ['error', 'fatal'];
